import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import PageHeader from 'components/common/PageHeader';
import IconButton from 'components/common/IconButton';
import {
  getSupplierDetails,
  searchSuppliers
} from 'services/supplier/supplierDashboardService';
import { getProductsBySupplierCode } from 'services/stock-location/productService';
import './productModal.css';

const columns = [
  { header: 'PART NO', accessor: 'partNo' },
  { header: 'DESCRIPTION', accessor: 'description' },
  { header: 'VPN', accessor: 'vpn' },
  { header: 'CATEGORY', accessor: 'category' },
  { header: 'CATEGORY2', accessor: 'category2' },
  { header: 'CATEGORY3', accessor: 'category3' },
  { header: 'PRIORITY', accessor: 'priority' },
  { header: 'VENDOR DESC', accessor: 'vendorDesc' },
  { header: 'PRIMARY VENDOR', accessor: 'primaryVendor' },
  { header: 'COST', accessor: 'cost' },
  { header: 'LEAD TIME', accessor: 'leadTime' },
  { header: 'MOQ', accessor: 'moq' },
  { header: 'ORDER MULTIPLE', accessor: 'orderMultiple' },
  { header: 'PLANNER NO', accessor: 'plannerNo' }
];

const mapData = items =>
  items.map(x => ({
    partNo: x.productCode,
    description: x.productDescription,
    vpn: x.vpn,
    category: x.category,
    category2: x.category2,
    category3: x.category3,
    priority: x.priority,
    vendorDesc: x.vendorDesc,
    primaryVendor: x.supplierCode,
    cost: Number(x.cost).toFixed(2),
    leadTime: x.leadTime,
    moq: x.moq,
    orderMultiple: x.orderMultiple,
    plannerNo: x.plannerNo
  }));

const priorityOptions = [
  { label: 'All', value: 'ALL' },
  { label: 'Null', value: 'Null' },
  { label: '0', value: '0' },
  { label: '1', value: '1' },
  { label: '2', value: '2' }
];

const SupplierProductsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [supplierName, setSupplierName] = useState('');
  const [supplierCode, setSupplierCode] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [priority, setPriority] = useState('ALL');
  const supplierOptions = useMemo(
    () =>
      suppliers.map(supplier => ({
        value: supplier.supplierId,
        label: `${supplier.supplierCode} - ${supplier.supplierName}`
      })),
    [suppliers]
  );
  const selectedSupplier =
    supplierOptions.find(option => String(option.value) === String(id)) ||
    null;
  const filteredData =
    priority === 'ALL'
      ? data
      : data.filter(row => {
          if (priority === 'Null') {
            return row.priority === null || row.priority === undefined;
          }

          return String(row.priority ?? '') === priority;
        });

  useEffect(() => {
    const loadSuppliers = async () => {
      const res = await searchSuppliers({
        pageNum: 1,
        pageSize: 1000,
        isLike: true,
        isGetAll: true,
        keyword: ''
      });

      setSuppliers(res?.items || []);
    };

    loadSuppliers();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        const detailRes = await getSupplierDetails(id);
        const detail = detailRes?.data || {};
        const code = detail?.supplierCode || '';

        setSupplierName(detail?.supplierName || '');
        setSupplierCode(code);

        if (!code) {
          setData([]);
          return;
        }

        const productRes = await getProductsBySupplierCode(code);
        const raw = productRes?.data || productRes || [];
        setData(mapData(raw));
      } catch (error) {
        console.error('Fetch supplier products error:', error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  const handleSupplierChange = option => {
    const supplierId = option?.value;

    if (!supplierId || String(supplierId) === String(id)) {
      return;
    }

    navigate(`/supplier/supplier-products/${supplierId}`);
  };

  const exportExcel = () => {
    const safeSupplierCode = (supplierCode || 'supplier-products').trim();
    const formatted = filteredData.map(row => {
      let obj = {};
      columns.forEach(col => {
        obj[col.header] = row[col.accessor];
      });
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(formatted);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Products');

    const excelBuffer = XLSX.write(wb, {
      bookType: 'xlsx',
      type: 'array'
    });

    const file = new Blob([excelBuffer], {
      type: 'application/octet-stream'
    });

    saveAs(
      file,
      `${safeSupplierCode}-${priority.toLowerCase()}.xlsx`
    );
  };

  const handlePrint = () => {
    const content = document.getElementById('print-area');
    const printWindow = window.open('', '', 'width=900,height=700');

    printWindow.document.write(`
      <html>
        <head>
          <title>Print</title>
          <style>
            body { font-family: Arial; padding: 20px; }
            table { width: 100%; border-collapse: collapse; }
            th { background: #3d6b2f; color: white; padding: 8px; border: 1px solid #000; }
            td { padding: 8px; border: 1px solid #000; text-align: center; }
            h3 { margin-bottom: 10px; }
          </style>
        </head>
        <body>
          <h3>Supplier Products / Vendor Master</h3>
          ${content?.innerHTML || ''}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  return (
    <>
      <PageHeader
        title="Supplier Products / Vendor Master"
        description={supplierName || 'View supplier product details'}
        className="mb-3"
        endEl={
          <div className="d-flex gap-2">
            <IconButton
              variant="falcon-default"
              size="sm"
              icon="chevron-left"
              transform="shrink-3"
              onClick={() => navigate('/supplier/supplier-dashboard')}
            >
              Back to Supplier Dashboard
            </IconButton>
            <IconButton
              variant="falcon-default"
              size="sm"
              icon="arrow-left"
              transform="shrink-3"
              onClick={() => navigate(`/supplier/supplier-details/${id}`)}
            >
              Back to Supplier
            </IconButton>
            <IconButton
              variant="falcon-default"
              size="sm"
              icon="history"
              transform="shrink-3"
              onClick={() =>
                navigate(`/supplier/purchasing-history?supplierId=${id}`)
              }
            >
              Go to Purchase History
            </IconButton>
          </div>
        }
      />

      <Row className="g-3">
        <Col lg={12}>
          <Card body>
            <Row className="align-items-end g-3 mb-3">
              <Col xs={12} sm={6} md={4} lg={4}>
                <Form.Group controlId="supplierProductsSupplier">
                  <Form.Label>Supplier</Form.Label>
                  <Select
                    inputId="supplierProductsSupplier"
                    options={supplierOptions}
                    value={selectedSupplier}
                    onChange={handleSupplierChange}
                    placeholder="Select supplier..."
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: base => ({
                        ...base,
                        zIndex: 9999
                      })
                    }}
                  />
                </Form.Group>
              </Col>

              <Col xs={12} sm={6} md={4} lg={2}>
                <Form.Group controlId="priorityFilter">
                  <Form.Label>Priority</Form.Label>
                  <Form.Select
                    value={priority}
                    onChange={event => setPriority(event.target.value)}
                  >
                    {priorityOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <div className="toolbar">
              <Button
                className="btn-green"
                onClick={exportExcel}
                disabled={loading || filteredData.length === 0}
              >
                Export Excel
              </Button>

              <Button
                className="btn-dark"
                onClick={handlePrint}
                disabled={loading || filteredData.length === 0}
              >
                Print / PDF
              </Button>
            </div>

            <div className="table-wrapper" id="print-area">
              {loading ? (
                <div style={{ textAlign: 'center', padding: 20 }}>
                  <Spinner animation="border" />
                </div>
              ) : (
                <table className="custom-table">
                  <thead>
                    <tr>
                      {columns.map((col, index) => (
                        <th key={index}>{col.header}</th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {filteredData.length > 0 ? (
                      filteredData.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {columns.map((col, colIndex) => (
                            <td key={colIndex}>{row[col.accessor]}</td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={columns.length}
                          style={{ textAlign: 'center' }}
                        >
                          No data
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default SupplierProductsPage;
